import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND, PRIVACY_EMAIL } from '../config/region';
import { t } from '../i18n';
import LegalPage from './LegalPage';
import { hasGlobalPrivacyControl } from '../utils/consent';

/*
 * Data-subject requests, and the CCPA/CPRA "Do Not Sell or Share My Personal Information" route.
 *
 * One page rather than two: under GDPR, the UK GDPR and the CCPA the same person is exercising the
 * same handful of rights, and splitting them by statute makes a visitor guess which regime they
 * fall under before they can ask a question.
 *
 * Requests arrive by email — the instruction explicitly allows it ("можно использовать тот же
 * email"), so there is no form to maintain. What the page does provide is the list of what a
 * request has to contain, so one arrives complete instead of costing a round-trip.
 */
const PrivacyRequestPage = () => {
  const gpc = hasGlobalPrivacyControl();

  return (
    <LegalPage
      title={t('Запросы по персональным данным')}
      description={t('Как получить доступ к своим данным, исправить или удалить их, а также отказаться от продажи и передачи персональных данных на платформе {0}.', BRAND)}
    >
      <p>
        {t('Вы можете в любой момент обратиться к нам по поводу своих персональных данных. Ниже описано, какие права у вас есть и как ими воспользоваться.')}
      </p>

      <h2>{t('Ваши права')}</h2>
      <ul>
        <li>{t('Доступ — получить подтверждение обработки и копию своих данных.')}</li>
        <li>{t('Исправление — потребовать уточнения неточных или неполных данных.')}</li>
        <li>{t('Удаление — потребовать удаления данных, если для их обработки не осталось законных оснований.')}</li>
        <li>{t('Переносимость — получить данные в структурированном машиночитаемом формате.')}</li>
        <li>{t('Возражение и отзыв согласия — прекратить обработку, основанную на согласии или на законном интересе.')}</li>
        <li>{t('Отказ от продажи и передачи данных — для жителей Калифорнии и других штатов с аналогичным законодательством.')}</li>
      </ul>
      <p>
        {t('Подробное описание оснований обработки, сроков хранения и получателей данных приведено в')}{' '}
        <Link to="/privacy">{t('Политике конфиденциальности')}</Link>.
      </p>

      <h2>{t('Do Not Sell or Share My Personal Information')}</h2>
      <p>
        {t('Мы не продаём персональные данные в традиционном смысле. Однако использование аналитических и рекламных cookie может считаться «продажей» или «передачей» данных по законодательству Калифорнии (CCPA/CPRA).')}
      </p>
      <p>
        {t('Отказаться можно двумя способами: отключить аналитические и маркетинговые категории в')}{' '}
        <Link to="/cookies">{t('настройках cookie')}</Link>
        {t(', либо направить нам запрос по электронной почте — см. раздел «Как подать запрос» ниже.')}
      </p>

      <h2>{t('Сигнал Global Privacy Control')}</h2>
      {gpc ? (
        <p className="ld-fallback">
          {t('✅ Ваш браузер передаёт сигнал Global Privacy Control, и мы его учли: аналитические и маркетинговые cookie на этом устройстве отключены, отдельный запрос не требуется.')}
        </p>
      ) : (
        <p>
          {t('Мы распознаём и учитываем сигнал Global Privacy Control (GPC). Если ваш браузер или расширение передаёт этот сигнал, аналитические и маркетинговые cookie отключаются автоматически, без каких-либо действий с вашей стороны. Сейчас этот сигнал в вашем браузере не обнаружен.')}
        </p>
      )}

      <h2>{t('Сроки рассмотрения')}</h2>
      <p>
        {t('Мы подтверждаем получение запроса в течение 1–3 рабочих дней и отвечаем по существу в срок, установленный применимым законодательством: не позднее одного месяца по GDPR и 45 дней по CCPA. Срок может быть продлён, о чём мы сообщим отдельно с указанием причины.')}
      </p>
      <p>
        {t('Чтобы защитить ваши данные, нам может потребоваться подтвердить вашу личность — например, попросив ответить с адреса электронной почты, привязанного к аккаунту.')}
      </p>

      <h2>{t('Как подать запрос')}</h2>
      <p>
        {t('Напишите нам на {0}. Чтобы мы могли обработать запрос без уточнений, укажите в письме:', PRIVACY_EMAIL)}
      </p>
      <ul>
        <li>{t('какое право вы реализуете — доступ, исправление, удаление, переносимость, возражение или отказ от продажи и передачи данных;')}</li>
        <li>{t('ваши имя и фамилию;')}</li>
        <li>{t('email, привязанный к аккаунту, — мы отвечаем только на него;')}</li>
        <li>{t('имя пользователя на платформе, если вы зарегистрированы;')}</li>
        <li>{t('уточнения: какие именно данные вас интересуют или что нужно исправить.')}</li>
      </ul>
      <p>
        <a className="lp-mail" href={`mailto:${PRIVACY_EMAIL}?subject=${encodeURIComponent(t('Запрос по персональным данным — {0}', BRAND))}`}>
          {t('Написать на {0}', PRIVACY_EMAIL)}
        </a>
      </p>

      <p>
        {t('Если вы считаете, что мы нарушили ваши права, вы вправе подать жалобу в надзорный орган по защите данных по месту жительства, работы или предполагаемого нарушения.')}
      </p>
    </LegalPage>
  );
};

export default PrivacyRequestPage;
